/** Adept-15 aspects and work styles. Aspect ids are lowercase slugs. */
export const aspectOrder = ["drive", "structure", "conceptual", "flexibility", "mastery", "ambition", "power", "assertiveness", "liveliness", "composure", "positivity", "awareness", "cooperativeness", "sensitivity", "humility"];
export const styleOrder = ["task", "adaptation", "achievement", "teamwork", "emotional", "interaction"];

const dictionary = {
  "en": {
    "aspects": {
      "drive": {
        "id": "drive",
        "title": "Drive",
        "definition": "Relaxed vs. Focused - Drive assesses reliability and task focus.",
        "low": [
          "Tends to be more relaxed and shifts more easily from goal to goal",
          "May tend to be less deadline-oriented and task-focused"
        ],
        "high": [
          "Tends to be dependable",
          "Hardworking, and accountable",
          "May get overly focused on narrow goals and task accomplishment"
        ]
      },
      "structure": {
        "id": "structure",
        "title": "Structure",
        "definition": "Big Picture vs. Detail-Oriented - Structure reflects the extent to which someone is planful, detail-oriented, and rule-conscious.",
        "low": [
          "Tends to focus on the big picture",
          "Comfortable with ambiguity",
          "May tend to be disorganized and less planful"
        ],
        "high": [
          "Tends to be careful",
          "Detail-focused, and orderly",
          "May be seen as perfectionist"
        ]
      },
      "conceptual": {
        "id": "conceptual",
        "title": "Conceptual",
        "definition": "Practical vs. Abstract - Conceptual is about being intellectually curious and imaginative.",
        "low": [
          "Tends to be concrete and practical",
          "More conventional and less curious"
        ],
        "high": [
          "Tends to be inquisitive and creative",
          "May be overly abstract and unrealistic"
        ]
      },
      "flexibility": {
        "id": "flexibility",
        "title": "Flexibility",
        "definition": "Consistent vs. Agile - Flexibility assesses adaptability and open-mindedness.",
        "low": [
          "May be more predictable as they seek tried-and-true approaches",
          "Can be inflexible and set in their ways"
        ],
        "high": [
          "Tends to be open to new ideas and experiences",
          "May come off as inconsistent or easily bored"
        ]
      },
      "mastery": {
        "id": "mastery",
        "title": "Mastery",
        "definition": "Doing vs. Improving - Mastery measures the extent to which someone is learning-oriented and improvement-focused.",
        "low": [
          "Is often more focused on getting things done",
          "Doing what they are good at; though they tend to be less concerned with continual self-development and believe people's skills do not often change much over time"
        ],
        "high": [
          "Tends to be focused on self-development",
          "Practicing to improve over time",
          "Has belief that others can improve; though they may be unrealistic in their views of others or their own potential"
        ]
      },
      "ambition": {
        "id": "ambition",
        "title": "Ambition",
        "definition": "Satisfied vs. Striving - Ambition reflects the extent to which someone is competitive, goal-oriented, and driven to succeed.",
        "low": [
          "Often more content with the current career status",
          "Generally has a good work-life balance, but may be less interested pursuing challenging goals"
        ],
        "high": [
          "Tends to focus on career achievements",
          "Are relentless in their pursuits",
          "May be obsessive and may pursue individual goals in instead of team goals."
        ]
      },
      "power": {
        "id": "power",
        "title": "Power",
        "definition": "Contributing vs. Leading - Power is the degree to which someone is controlling, directive, and motivated to lead.",
        "low": [
          "Tends to be team player, lead by example",
          "Is willing to let others take control"
        ],
        "high": [
          "Tends to be interested in leadership",
          "Exercises control, and influence"
        ]
      },
      "assertiveness": {
        "id": "assertiveness",
        "title": "Assertiveness",
        "definition": "Cautious vs. Socially Bold - Assertiveness is the extent to which someone is outspoken, dominant, and socially confident.",
        "low": [
          "Tends to be more cautious in sharing opinions",
          "Is less likely to offend others",
          "May give in too easily and appear indecisive at times"
        ],
        "high": [
          "Is often bold and decisive",
          "Can be confrontational and aggressive"
        ]
      },
      "liveliness": {
        "id": "liveliness",
        "title": "Liveliness",
        "definition": "Reserved vs. Outgoing - Liveliness measures how outgoing and energetic someone seems.",
        "low": [
          "Tends to be more reserved and quiet",
          "More comfortable being by themselves and working alone"
        ],
        "high": [
          "Tends to be sociable and friendly",
          "May be seen as rambunctious and attention-seeking"
        ]
      },
      "composure": {
        "id": "composure",
        "title": "Composure",
        "definition": "Passionate vs. Calm - Composure is the degree to which someone is calm and even-tempered.",
        "low": [
          "Tends to demonstrate passion and excitement",
          "May be seen as irritable and unpredictable"
        ],
        "high": [
          "Tends to be emotionally controlled",
          "Can seem aloof and dispassionate",
          "Calm under pressure"
        ]
      },
      "positivity": {
        "id": "positivity",
        "title": "Positivity",
        "definition": "Worried vs. Hopeful - Positivity reflects the extent to which someone is happy, optimistic, and resilient.",
        "low": [
          "Tends to be realistic and recognize potential drawbacks",
          "May also be seen as pessimistic and anxious at times"
        ],
        "high": [
          "Tends to be hopeful and positive",
          "May downplay or disregard potential problems"
        ]
      },
      "awareness": {
        "id": "awareness",
        "title": "Awareness",
        "definition": "Indifferent vs. Self-Aware - Awareness speaks to one's willingness to reflect and be self-aware.",
        "low": [
          "May have a static self-concept and be resistant to feedback",
          "Are less concerned with or care what others think about them"
        ],
        "high": [
          "Tends to be introspective and know own strengths and weaknesses",
          "May be too self-critical"
        ]
      },
      "cooperativeness": {
        "id": "cooperativeness",
        "title": "Cooperativeness",
        "definition": "Independent vs. Team-Oriented - Cooperativeness reflects on whether people are cooperative and trusting.",
        "low": [
          "Tends to be more independent",
          "Often prefers working alone",
          "Is generally less interested in teamwork and collaboration"
        ],
        "high": [
          "Tends to be team-oriented and accommodating",
          "Can sometimes be taken advantage of by others"
        ]
      },
      "sensitivity": {
        "id": "sensitivity",
        "title": "Sensitivity",
        "definition": "Stoic vs. Compassionate - Sensitivity is the extent to which someone is sympathetic, caring, and understanding.",
        "low": [
          "Tends to be tough-minded and unsentimental",
          "Is candid and direct"
        ],
        "high": [
          "Tends to be kind",
          "Compassionate and tolerant",
          "May have difficulty providing negative feedback or being firm with others"
        ]
      },
      "humility": {
        "id": "humility",
        "title": "Humility",
        "definition": "Proud vs. Modest - Humility combines modesty and sincerity.",
        "low": [
          "Tends to be proud, cunning, and effective at managing situations requiring tact and posturing",
          "Can also be seen manipulative at times"
        ],
        "high": [
          "Tends to be humble and honest",
          "May be less effective in advocating for their own interests at work"
        ]
      }
    },
    "styles": {
      "task": {
        "id": "task",
        "title": "Task",
        "description": "Reflects how individuals approach tasks, balancing between being relaxed and highly focused, and whether they prefer big-picture thinking or focusing on details.",
        "aspectIds": [
          "drive",
          "structure"
        ]
      },
      "adaptation": {
        "id": "adaptation",
        "title": "Adaptation",
        "description": "Assesses how individuals handle change and complexity, including their practical versus abstract thinking, consistency versus flexibility, and focus on maintaining versus improving processes.",
        "aspectIds": [
          "conceptual",
          "flexibility",
          "mastery"
        ]
      },
      "achievement": {
        "id": "achievement",
        "title": "Achievement",
        "description": "Gauges an individual’s drive for success, including their level of ambition and preference for contributing versus controlling in achieving their goals.",
        "aspectIds": [
          "ambition",
          "power"
        ]
      },
      "teamwork": {
        "id": "teamwork",
        "title": "Teamwork",
        "description": "Measures how individuals collaborate with others, including their tendency to work independently versus being team-oriented, their sensitivity to others' emotions, and their humility.",
        "aspectIds": [
          "cooperativeness",
          "sensitivity",
          "humility"
        ]
      },
      "emotional": {
        "id": "emotional",
        "title": "Emotional",
        "description": "Evaluates how individuals manage and express their emotions, balancing passion with calmness, concern with optimism, and being insulated with self-awareness.",
        "aspectIds": [
          "composure",
          "positivity",
          "awareness"
        ]
      },
      "interaction": {
        "id": "interaction",
        "title": "Interaction",
        "description": "Measures how individuals engage in social interactions, from being cautious to socially bold, and from reserved to outgoing.",
        "aspectIds": [
          "assertiveness",
          "liveliness"
        ]
      }
    }
  },
  "de": {
    "aspects": {
      "drive": {
        "id": "drive",
        "title": "Antrieb",
        "definition": "Entspannt vs. fokussiert – Antrieb erfasst Zuverlässigkeit und Aufgabenfokus.",
        "low": [
          "Neigt dazu, entspannter zu sein und leichter von Ziel zu Ziel zu wechseln",
          "Könnte weniger fristen- und aufgabenorientiert sein"
        ],
        "high": [
          "Gilt als verlässlich",
          "Arbeitsam und verantwortlich",
          "Könnte sich zu stark auf enge Ziele und Aufgabenerledigung konzentrieren"
        ]
      },
      "structure": {
        "id": "structure",
        "title": "Struktur",
        "definition": "Ganzheitlich vs. detailorientiert – Struktur beschreibt, inwieweit jemand planvoll, detailorientiert und regelbewusst vorgeht.",
        "low": [
          "Richtet den Blick eher auf das Gesamtbild",
          "Kommt mit Mehrdeutigkeit gut zurecht",
          "Könnte unorganisiert und weniger planvoll wirken"
        ],
        "high": [
          "Geht eher sorgfältig vor",
          "Detailorientiert und ordentlich",
          "Könnte als perfektionistisch wahrgenommen werden"
        ]
      },
      "conceptual": {
        "id": "conceptual",
        "title": "Konzeptionell",
        "definition": "Praktisch vs. abstrakt – Konzeptionell beschreibt intellektuelle Neugier und Vorstellungsvermögen.",
        "low": [
          "Ist eher konkret und praxisorientiert",
          "Konventioneller und weniger neugierig"
        ],
        "high": [
          "ist eher wissbegierig und kreativ",
          "Könnte zu abstrakt und wirklichkeitsfern sein"
        ]
      },
      "flexibility": {
        "id": "flexibility",
        "title": "Flexibilität",
        "definition": "Beständig vs. agil – Flexibilität erfasst Anpassungsfähigkeit und Aufgeschlossenheit.",
        "low": [
          "Könnte berechenbarer wirken, weil  bewährte Wege bevorzugt werden",
          "Könnte unflexibel und festgefahren sein"
        ],
        "high": [
          "Ist offen für neue Ideen und Erfahrungen",
          "Könnte als sprunghaft oder schnell gelangweilt wahrgenommen werden"
        ]
      },
      "mastery": {
        "id": "mastery",
        "title": "Lernbereitschaft",
        "definition": "Machen vs. verbessern – Lernbereitschaft misst, inwieweit jemand lernorientiert und auf Weiterentwicklung ausgerichtet ist.",
        "low": [
          "Konzentriert sich oft stärker darauf, Dinge zu erledigen",
          "Tutm was man gut kann; kümmert sich weniger um fortlaufende Selbstentwicklung und gehen davon aus, dass sich Fähigkeiten anderer kaum verändern"
        ],
        "high": [
          "Richtet den Fokus auf die eigene Weiterentwicklung",
          "Übt, um sich über die Zeit zu verbessern",
          "Glaubt, dass andere sich weiterentwickeln können; kann dabei das Potenzial anderer oder das eigene überschätzen"
        ]
      },
      "ambition": {
        "id": "ambition",
        "title": "Ehrgeiz",
        "definition": "Zufrieden vs. strebsam – Ehrgeiz beschreibt, inwieweit jemand wettbewerbsorientiert, zielgerichtet und erfolgsgetrieben ist.",
        "low": [
          "Ist oft zufriedener mit dem aktuellen Stand der Laufbahn",
          "Hat in der Regel eine gute Work-Life-Balance, interessiert sich aber weniger für anspruchsvolle Ziele"
        ],
        "high": [
          "Richtet den Fokus auf berufliche Erfolge",
          "Verfolgt eigene Ziele unnachgiebig",
          "Kann zwanghaft wirken und individuelle Ziele statt Teamziele verfolgen."
        ]
      },
      "power": {
        "id": "power",
        "title": "Einflussnahme",
        "definition": "Mitwirkend vs. leitend – Einflussnahme beschreibt, inwieweit jemand steuernd, direktiv und führungsmotiviert ist.",
        "low": [
          "Ist eher Teamplayer und führt durch Vorbild",
          "Lässt andere bereitwillig die Steuerung übernehmen"
        ],
        "high": [
          "Interessiert sich für Führung",
          "Übt Kontrolle und Einfluss aus"
        ]
      },
      "assertiveness": {
        "id": "assertiveness",
        "title": "Durchsetzungsvermögen",
        "definition": "Vorsichtig vs. herausfordernd – Durchsetzungsvermögen beschreibt, inwieweit jemand offen, dominant und sozial selbstsicher ist.",
        "low": [
          "Ist beim Teilen von Meinungen eher zurückhaltend",
          "Verletzt andere seltener",
          "Gibt mitunter zu leicht nach und wirkt unentschlossen"
        ],
        "high": [
          "Ist oft mutig und entscheidungsstark",
          "Könnte konfrontativ und aggressiv auftreten"
        ]
      },
      "liveliness": {
        "id": "liveliness",
        "title": "Lebhaftigkeit",
        "definition": "Reserviert vs. aufgeschlossen – Lebhaftigkeit misst, wie gesellig und energiegeladen jemand wirkt.",
        "low": [
          "Ist eher zurückhaltend und ruhig",
          "Fühlt sich wohler allein und bei unabhängiger Arbeit"
        ],
        "high": [
          "Ist gesellig und freundlich",
          "Könnte als überschwänglich und aufmerksamkeitsbedürftig wahrgenommen werden"
        ]
      },
      "composure": {
        "id": "composure",
        "title": "Gelassenheit",
        "definition": "Leidenschaftlich vs. gelassen – Gelassenheit beschreibt, inwieweit jemand ruhig und ausgeglichen ist.",
        "low": [
          "Zeigt eher Leidenschaft und Begeisterung",
          "Kann als reizbar und unberechenbar wahrgenommen werden"
        ],
        "high": [
          "Wirkt emotional kontrolliert",
          "Kann distanziert und leidenschaftslos erscheinen",
          "Ruhig unter Druck"
        ]
      },
      "positivity": {
        "id": "positivity",
        "title": "Positivität",
        "definition": "Beunruhigt vs. hoffnungsvoll – Positivität beschreibt, inwieweit jemand zuversichtlich, optimistisch und widerstandsfähig ist.",
        "low": [
          "Ist eher realistisch und erkennt mögliche Nachteile",
          "Könnte zeitweise als pessimistisch und ängstlich wahrgenommen werden"
        ],
        "high": [
          "Ist hoffnungsvoll und positiv",
          "Könnte mögliche Probleme herunterspielen oder ausblenden"
        ]
      },
      "awareness": {
        "id": "awareness",
        "title": "Selbstreflexion",
        "definition": "Gleichgültig vs. sich selbst bewußt – Selbstreflexion beschreibt die Bereitschaft zur Reflexion und zur eigenen Bewusstheit.",
        "low": [
          "Kann ein starres Selbstbild haben und Feedback nicht annehmen",
          "Kümmert sich weniger darum, was andere über einen denken"
        ],
        "high": [
          "Ist introspektiv und kennt die eigenen Stärken und Schwächen",
          "Kann zu selbstkritisch sein"
        ]
      },
      "cooperativeness": {
        "id": "cooperativeness",
        "title": "Kooperationsbereitschaft",
        "definition": "Unabhängig vs. teamorientiert – Kooperationsbereitschaft beschreibt, inwieweit Menschen kooperativ und vertrauensvoll sind.",
        "low": [
          "Ist eher unabhängig",
          "Arbeitet oft lieber allein",
          "Interessiert sich insgesamt weniger für Teamarbeit und Zusammenarbeit"
        ],
        "high": [
          "Ist teamorientiert und entgegenkommend",
          "Kann von anderen mitunter ausgenutzt werden"
        ]
      },
      "sensitivity": {
        "id": "sensitivity",
        "title": "Einfühlungsvermögen",
        "definition": "Distanziert vs. mitfühlend – Einfühlungsvermögen beschreibt, inwieweit jemand einfühlsam, fürsorglich und verständnisvoll ist.",
        "low": [
          "Ist eher hartnäckig und unsensibel",
          "Ist offen und direkt"
        ],
        "high": [
          "Ist eher freundlich",
          "Mitfühlend und tolerant",
          "Kann es schwierig finden, negatives Feedback zu geben oder konsequent aufzutreten"
        ]
      },
      "humility": {
        "id": "humility",
        "title": "Bescheidenheit",
        "definition": "Stolz vs. umgänglich – Bescheidenheit verbindet Demut und Aufrichtigkeit.",
        "low": [
          "Ist eher stolz, gewitzt und wirksam in Situationen, die Takt und Inszenierung erfordern",
          "Kann zeitweise als manipulativ wahrgenommen werden"
        ],
        "high": [
          "Ist bescheiden und ehrlich",
          "Setzt die eigenen Interessen am Arbeitsplatz weniger wirksam durch"
        ]
      }
    },
    "styles": {
      "task": {
        "id": "task",
        "title": "Aufgabe",
        "description": "Beschreibt, wie Personen Aufgaben angehen: zwischen entspannt und stark fokussiert sowie zwischen Denken im Gesamtbild und Fokus auf Details.",
        "aspectIds": [
          "drive",
          "structure"
        ]
      },
      "adaptation": {
        "id": "adaptation",
        "title": "Anpassung",
        "description": "Erfasst, wie Personen mit Wandel und Komplexität umgehen – einschließlich praktischen versus abstrakten Denkens, Beständigkeit versus Flexibilität sowie Fokus auf Beibehalten versus Verbessern von Prozessen.",
        "aspectIds": [
          "conceptual",
          "flexibility",
          "mastery"
        ]
      },
      "achievement": {
        "id": "achievement",
        "title": "Leistung",
        "description": "Misst den Erfolgswillen einer Person, einschließlich Ehrgeiz und der Tendenz, beim Erreichen von Zielen eher beizutragen oder zu steuern.",
        "aspectIds": [
          "ambition",
          "power"
        ]
      },
      "teamwork": {
        "id": "teamwork",
        "title": "Teamarbeit",
        "description": "Misst, wie Personen mit anderen zusammenarbeiten – einschließlich der Tendenz, unabhängig versus teamorientiert zu arbeiten, der Sensibilität für die Emotionen anderer und der Bescheidenheit.",
        "aspectIds": [
          "cooperativeness",
          "sensitivity",
          "humility"
        ]
      },
      "emotional": {
        "id": "emotional",
        "title": "Emotional",
        "description": "Bewertet, wie Personen Emotionen steuern und ausdrücken – im Spannungsfeld von Leidenschaft und Ruhe, Besorgnis und Optimismus sowie Abgeschirmtheit und Selbstwahrnehmung.",
        "aspectIds": [
          "composure",
          "positivity",
          "awareness"
        ]
      },
      "interaction": {
        "id": "interaction",
        "title": "Interaktion",
        "description": "Misst, wie Personen soziale Interaktionen gestalten – von vorsichtig bis sozial mutig und von zurückhaltend bis extravertiert.",
        "aspectIds": [
          "assertiveness",
          "liveliness"
        ]
      }
    }
  },
  "fi": {
    "aspects": {
      "drive": {
        "id": "drive",
        "title": "Määrätietoisuus",
        "definition": "Rento vs. keskittynyt - Määrätietoisuus arvioi luotettavuutta ja tehtäviin keskittymistä.",
        "low": [
          "On yleensä rennompi ja siirtyy helpommin tavoitteesta toiseen",
          "Voi olla vähemmän aikataulu- ja tehtäväkeskeinen"
        ],
        "high": [
          "On yleensä luotettava",
          "Ahkera ja vastuullinen",
          "Voi keskittyä liikaa kapeisiin tavoitteisiin ja tehtävien suorittamiseen"
        ]
      },
      "structure": {
        "id": "structure",
        "title": "Rakenne",
        "definition": "Kokonaisuuksia hahmottava vs. yksityiskohtiin keskittyvä - Rakenne kuvaa, kuinka suunnitelmallinen, yksityiskohtiin keskittyvä ja sääntöjä noudattava henkilö on.",
        "low": [
          "Keskittyy yleensä kokonaiskuvaan",
          "Sietää hyvin epäselviä tilanteita",
          "Voi olla epäjärjestelmällinen ja vähemmän suunnitelmallinen"
        ],
        "high": [
          "On yleensä huolellinen",
          "Yksityiskohtiin keskittyvä ja järjestelmällinen",
          "Voi vaikuttaa perfektionistilta"
        ]
      },
      "conceptual": {
        "id": "conceptual",
        "title": "Käsitteellinen",
        "definition": "Käytännöllinen vs. abstrakti - Käsitteellisyys kuvaa älyllistä uteliaisuutta ja mielikuvituksellisuutta.",
        "low": [
          "On yleensä konkreettinen ja käytännöllinen",
          "Perinteisempi ja vähemmän utelias"
        ],
        "high": [
          "On yleensä utelias ja luova",
          "Voi olla liian abstrakti ja epärealistinen"
        ]
      },
      "flexibility": {
        "id": "flexibility",
        "title": "Joustavuus",
        "definition": "Johdonmukainen vs. ketterä - Joustavuus arvioi sopeutumiskykyä ja ennakkoluulottomuutta.",
        "low": [
          "Voi olla ennustettavampi, koska suosii hyviksi havaittuja toimintatapoja",
          "Voi olla joustamaton ja juuttunut omiin tapoihinsa"
        ],
        "high": [
          "On yleensä avoin uusille ideoille ja kokemuksille",
          "Voi vaikuttaa epäjohdonmukaiselta tai helposti tylsistyvältä"
        ]
      },
      "mastery": {
        "id": "mastery",
        "title": "Kehittymishalu",
        "definition": "Tekeminen vs. kehittäminen - Kehittymishalu mittaa, kuinka oppimissuuntautunut ja kehittymiseen keskittynyt henkilö on.",
        "low": [
          "Keskittyy usein ennen kaikkea asioiden loppuun saattamiseen",
          "Tekee sitä, missä on hyvä, mutta on yleensä vähemmän kiinnostunut jatkuvasta itsensä kehittämisestä ja uskoo, etteivät ihmisten taidot juuri muutu ajan myötä"
        ],
        "high": [
          "Keskittyy yleensä itsensä kehittämiseen",
          "Harjoittelee kehittyäkseen ajan mittaan",
          "Uskoo, että muut voivat kehittyä, vaikka voi suhtautua epärealistisesti muiden tai omaan potentiaaliinsa"
        ]
      },
      "ambition": {
        "id": "ambition",
        "title": "Kunnianhimo",
        "definition": "Tyytyväinen vs. tavoitteellinen - Kunnianhimo kuvaa, kuinka kilpailuhenkinen, tavoitekeskeinen ja menestymishaluinen henkilö on.",
        "low": [
          "Usein tyytyväisempi nykyiseen uratilanteeseensa",
          "Työ- ja yksityiselämä on yleensä hyvin tasapainossa, mutta voi olla vähemmän kiinnostunut pyrkimään haastaviin tavoitteisiin"
        ],
        "high": [
          "Keskittyy yleensä urasaavutuksiin",
          "On periksiantamaton pyrkimyksissään",
          "Voi olla pakkomielteinen ja tavoitella omia tavoitteitaan tiimin tavoitteiden sijaan."
        ]
      },
      "power": {
        "id": "power",
        "title": "Valta",
        "definition": "Osallistuminen vs. johtaminen - Valta kuvaa, missä määrin henkilö on kontrolloiva, ohjaava ja motivoitunut johtamaan.",
        "low": [
          "On yleensä tiimipelaaja ja johtaa esimerkillään",
          "On valmis antamaan muiden ottaa ohjat"
        ],
        "high": [
          "On yleensä kiinnostunut johtajuudesta",
          "Ottaa ohjat käsiinsä ja vaikuttaa muihin"
        ]
      },
      "assertiveness": {
        "id": "assertiveness",
        "title": "Jämäkkyys",
        "definition": "Varovainen vs. sosiaalisesti rohkea - Jämäkkyys kuvaa, missä määrin henkilö on suorasanainen, hallitseva ja sosiaalisesti itsevarma.",
        "low": [
          "On yleensä varovaisempi mielipiteidensä esittämisessä",
          "Loukkaa muita harvemmin",
          "Voi antaa periksi liian helposti ja vaikuttaa toisinaan päättämättömältä"
        ],
        "high": [
          "On usein rohkea ja päättäväinen",
          "Voi ajautua vastakkainasetteluihin ja olla aggressiivinen"
        ]
      },
      "liveliness": {
        "id": "liveliness",
        "title": "Eloisuus",
        "definition": "Varautunut vs. seurallinen - Eloisuus mittaa, kuinka seuralliselta ja energiseltä henkilö vaikuttaa.",
        "low": [
          "Tavallisesti varautunut ja hiljainen",
          "Viihtyy paremmin omissa oloissaan ja työskentelee mieluiten yksin"
        ],
        "high": [
          "Tavallisesti seurallinen ja ystävällinen",
          "Voi vaikuttaa riehakkaalta ja huomionhakuiselta"
        ]
      },
      "composure": {
        "id": "composure",
        "title": "Tyyneys",
        "definition": "Intohimoinen vs. rauhallinen - Tyyneys kuvaa, kuinka rauhallinen ja tasainen henkilö on.",
        "low": [
          "Näyttää yleensä intohimonsa ja innostuksensa",
          "Voi vaikuttaa ärtyisältä ja arvaamattomalta"
        ],
        "high": [
          "Hallitsee yleensä tunteensa hyvin",
          "Voi vaikuttaa etäiseltä ja välinpitämättömältä",
          "Säilyttää rauhallisuutensa paineen alla"
        ]
      },
      "positivity": {
        "id": "positivity",
        "title": "Positiivisuus",
        "definition": "Huolestunut vs. toiveikas - Positiivisuus kuvaa, kuinka iloinen, optimistinen ja palautumiskykyinen henkilö on.",
        "low": [
          "Tavallisesti realistinen ja tunnistaa mahdolliset haittapuolet",
          "Voi myös ajoittain vaikuttaa pessimistiseltä ja ahdistuneelta"
        ],
        "high": [
          "Tavallisesti toiveikas ja positiivinen",
          "Voi vähätellä tai sivuuttaa mahdollisia ongelmia"
        ]
      },
      "awareness": {
        "id": "awareness",
        "title": "Itsetuntemus",
        "definition": "Välinpitämätön vs. itsetutkiskeleva - Itsetuntemus kuvaa halukkuutta pohtia omaa toimintaa ja tuntea itsensä.",
        "low": [
          "Voi pitää kiinni muuttumattomasta minäkuvastaan ja torjua palautetta",
          "Ei juuri välitä siitä, mitä muut hänestä ajattelevat"
        ],
        "high": [
          "On yleensä itsetutkiskeleva ja tuntee omat vahvuutensa ja heikkoutensa",
          "Voi olla liian itsekriittinen"
        ]
      },
      "cooperativeness": {
        "id": "cooperativeness",
        "title": "Yhteistyöhenkisyys",
        "definition": "Itsenäinen vs. tiimilähtöinen - Yhteistyökyky kuvaa, kuinka yhteistyöhaluinen ja luottavainen henkilö on.",
        "low": [
          "On yleensä itsenäisempi",
          "Työskentelee usein mieluiten yksin",
          "On yleensä vähemmän kiinnostunut tiimityöstä ja yhteistyöstä"
        ],
        "high": [
          "On yleensä tiimilähtöinen ja joustava",
          "Muut voivat joskus käyttää häntä hyväkseen"
        ]
      },
      "sensitivity": {
        "id": "sensitivity",
        "title": "Herkkyys",
        "definition": "Stoalainen vs. myötätuntoinen - Herkkyys kuvaa, kuinka myötätuntoinen, välittävä ja ymmärtäväinen henkilö on.",
        "low": [
          "On yleensä jämäkkä ja tunteilematon",
          "On rehellinen ja suora"
        ],
        "high": [
          "On yleensä hyväntahtoinen",
          "Myötätuntoinen ja suvaitsevainen",
          "Voi kokea vaikeaksi antaa kielteistä palautetta tai olla jämäkkä muita kohtaan"
        ]
      },
      "humility": {
        "id": "humility",
        "title": "Nöyryys",
        "definition": "Ylpeä vs. vaatimaton - Nöyryys yhdistää vaatimattomuuden ja vilpittömyyden.",
        "low": [
          "On yleensä ylpeä, ovela ja taitava hallitsemaan tilanteita, jotka vaativat tahdikkuutta ja oman aseman korostamista",
          "Voi myös joskus vaikuttaa manipuloivalta"
        ],
        "high": [
          "On yleensä nöyrä ja rehellinen",
          "Voi olla vähemmän tehokas omien etujensa ajamisessa työssä"
        ]
      }
    },
    "styles": {
      "task": {
        "id": "task",
        "title": "Tehtävä",
        "description": "Kuvaa, miten henkilöt lähestyvät tehtäviä, missä he sijoittuvat rentouden ja vahvan keskittymisen välillä sekä suosivatko he kokonaiskuvan hahmottamista vai yksityiskohtiin keskittymistä.",
        "aspectIds": [
          "vietti",
          "rakenne"
        ]
      },
      "adaptation": {
        "id": "adaptation",
        "title": "Sopeutuminen",
        "description": "Arvioi, miten henkilöt käsittelevät muutosta ja monimutkaisuutta: käytännöllinen vs. abstrakti ajattelu, johdonmukaisuus vs. joustavuus sekä prosessien ylläpitäminen vs. kehittäminen.",
        "aspectIds": [
          "konseptuaalinen",
          "joustavuus",
          "hallinta"
        ]
      },
      "achievement": {
        "id": "achievement",
        "title": "Saavutus",
        "description": "Arvioi henkilön menestymishalua, kuten kunnianhimon tasoa ja sitä, haluaako hän tavoitteitaan saavuttaessaan mieluummin osallistua vai kontrolloida.",
        "aspectIds": [
          "ammattitaito",
          "valta"
        ]
      },
      "teamwork": {
        "id": "teamwork",
        "title": "Tiimityö",
        "description": "Mittaa, miten henkilöt tekevät yhteistyötä muiden kanssa: taipumusta työskennellä itsenäisesti tai tiimilähtöisesti, herkkyyttä muiden tunteille sekä nöyryyttä.",
        "aspectIds": [
          "yhteistyökyky",
          "herkkyys",
          "nöyryys"
        ]
      },
      "emotional": {
        "id": "emotional",
        "title": "Tunne-elämä",
        "description": "Arvioi, miten henkilöt hallitsevat ja ilmaisevat tunteitaan: missä he sijoittuvat intohimon ja rauhallisuuden, huolestuneisuuden ja optimismin sekä välinpitämättömyyden ja itsetuntemuksen välillä.",
        "aspectIds": [
          "tasapainoisuus",
          "positiivisuus",
          "tietoisuus"
        ]
      },
      "interaction": {
        "id": "interaction",
        "title": "Vuorovaikutus",
        "description": "Mittaa, miten henkilöt osallistuvat sosiaaliseen vuorovaikutukseen, varovaisesta sosiaalisesti rohkeaan ja varautuneesta ulospäin suuntautuneeseen.",
        "aspectIds": [
          "itseluottamus",
          "elämänilo"
        ]
      }
    }
  }
};

export default dictionary;
